import React from 'react';
import PropTypes from 'prop-types';
import { Client } from '@weezevent/weezbox-client';
import { fetcher, useForm } from '@weezevent/weezjs-core';
import { useTranslation } from 'react-i18next';
import useAppContext from './useAppContext';
import { ToastContext, Modal, Button, Message, SelectLabel, TooltipHelp } from '@weezevent/nacre';

import Style from '../pages/attendee/post-action/rotateBarcodePostAction.module.css';

export const PrinterContext = React.createContext(null);

function PrinterSelection({ client, onChange, printers, form }) {
    const { t } = useTranslation();

    const toast = React.useContext(ToastContext);

    const manufacturers = [
        {
            text: t('ticket.manager.common.action.print_ticket.printer_type.zebra'),
            value: 0,
            matches: [/Zebra/gi]
        },
        {
            text: t('ticket.manager.common.action.print_ticket.printer_type.boca'),
            value: 1,
            matches: [/Boca/gi]
        },
        {
            text: t('ticket.manager.common.action.print_ticket.printer_type.evolis'),
            value: 2,
            matches: [/Evolis/gi]
        }
    ];

    const supportTypes = React.useMemo(() => {
        return [
            {
                text: t('ticket.manager.common.action.print_ticket.support_type.roll'),
                value: 3,
                manufacturers: [0]
            },
            {
                text: t('ticket.manager.common.action.print_ticket.support_type.carton_v1'),
                value: 4,
                manufacturers: [0]
            },
            {
                text: t('ticket.manager.common.action.print_ticket.support_type.carton_v2'),
                value: 5,
                manufacturers: [0]
            },
            {
                text: t('ticket.manager.common.action.print_ticket.support_type.roll_v2'),
                value: 6,
                manufacturers: [0]
            },
            {
                text: t('ticket.manager.common.action.print_ticket.support_type.tradeshow'),
                value: 7,
                manufacturers: [0]
            },
            {
                text: t('ticket.manager.common.action.print_ticket.support_type.evolis'),
                value: 8,
                manufacturers: [2]
            },
            {
                text: t('ticket.manager.common.action.print_ticket.support_type.carton_us'),
                value: 9,
                manufacturers: [0]
            },
            {
                text: t('ticket.manager.common.action.print_ticket.support_type.wristband'),
                value: 10,
                manufacturers: [0]
            },
            {
                text: t('ticket.manager.common.action.print_ticket.support_type.generic'),
                value: 13,
                manufacturers: [0, 1, 2]
            }
        ];
    }, [t]);

    const printersOptions = React.useMemo(() => {
        return printers.map(printer => {
            return { text: printer.additionnalData.manufacturer, value: printer, key: printer.printerId };
        });
    }, [printers]);

    // Filter support types for every printer type
    const supportTypeOptions = React.useMemo(() => {
        const available_types = supportTypes.filter(({ manufacturers }) => {
            return manufacturers === null || manufacturers.includes(form.manufacturer);
        });

        return available_types;
    }, [form.manufacturer, supportTypes]);

    if (!printers.length) {
        return (
            <Message className={Style['warning_message']} type="warning" title={t('ticket.modal.no_printers_title')}>
                {t('ticket.modal.no_printers_description')}
                <div className={Style['info-text']}>{t('ticket.manager.common.action.print_ticket.info-text')}</div>
            </Message>
        );
    }

    return (
        <div>
            <div className={Style['checkbox-and-tooltip']}>
                <SelectLabel
                    label={t('ticket.manager.common.action.print_ticket.checkbox_printer_choice')}
                    placeholder={t('ticket.printer.choose_printer')}
                    options={printersOptions}
                    onChange={value => {
                        const { manufacturer } = value.additionnalData;
                        const weez_id = manufacturers.find(({ matches }) => matches.some(regex => regex.test(manufacturer)));
                        if (!weez_id) {
                            toast.error(t('ticket.printer.unknown_manufacturer'));
                            return;
                        }

                        onChange('manufacturer', weez_id.value);
                        client.selectPrinter(value.printerId);
                    }}
                />
                <TooltipHelp triggerOnHover position="bottom" title={t('ticket.manager.common.action.print_ticket.checkbox_printer_type_tooltip')} />
            </div>
            <div className={Style['checkbox-and-tooltip']}>
                <SelectLabel
                    label={t('ticket.manager.common.action.print_ticket.checkbox_support_type')}
                    placeholder={t('ticket.printer.choose_support_type')}
                    value={form.support_type}
                    options={supportTypeOptions}
                    onChange={value => onChange('support_type', value)}
                />
                <TooltipHelp triggerOnHover position="bottom" title={t('ticket.manager.common.action.print_ticket.checkbox_support_type_tooltip')} />
            </div>
        </div>
    );
}

const check_message_size = (payload, t) => {
    // MAX_SIZE is defined by aws api_gateway and is set at 128kB;
    const MAX_SIZE = 128_000;

    // DEFAULT_SIZE is the size of the message + the token "more or less". It does not include payload
    const DEFAULT_SIZE = 365; // bytes

    const payload_size = Int8Array.from(payload).byteLength;

    const message_size = payload_size + DEFAULT_SIZE;
    if (message_size > MAX_SIZE) {
        throw new Error(t('ticket.printer.max_size_reached', { size: Math.ceil(message_size / 1000) }));
    }
};

// Default wait time is 7 seconds for every print
const DEFAULT_WAIT_TIME = 7_000;

const wait = ms => {
    return new Promise(resolve => {
        setTimeout(() => resolve(), ms);
    });
};

const print_recursive = (callbacks, index = 0) => {
    // We finished printing
    if (!callbacks[index]) {
        return Promise.resolve();
    }

    const promise = callbacks[index];

    return promise(index).finally(() => {
        return print_recursive(callbacks, index + 1);
    });
};

function PrintDialogBase({ printerContext, client, onSave, onCancel, getDataCallback }) {
    const { t } = useTranslation();
    const [hasLoaded, setHasLoaded] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const toast = React.useContext(ToastContext);

    const [form, setForm] = useForm({
        manufacturer: printerContext.printer_type,
        support_type: printerContext.support_type
    });

    const [printers, setPinters] = React.useState([]);
    const [currentPrintJob, setCurrentJob] = React.useState(0);

    React.useEffect(() => {
        client.requestPrintersUpdate();
        const subscription = client.printers$.subscribe(printers => {
            setHasLoaded(true);
            setPinters(printers);
        });

        return () => {
            subscription.unsubscribe();
        };
    }, [client]);

    const handleSubmit = React.useCallback(() => {
        setIsSubmitting(true);
        if (!Array.isArray(getDataCallback)) {
            getDataCallback = [getDataCallback];
        }

        const errors = [];
        // callbacks is a list of closures
        // containing the call to get_data + print
        // and a simple wait 7 sec before before
        const callbacks = getDataCallback.map(getData => {
            return index => {
                const wait_time = index === 0 ? 0 : DEFAULT_WAIT_TIME;
                // we wait (7 sec + get_data time) in between every call
                return wait(wait_time)
                    .then(() => {
                        // let's update the counter here to be more precise
                        setCurrentJob(index + 1);

                        return getData({
                            manufacturer: form.manufacturer,
                            support_type: form.support_type
                        });
                    })
                    .then(data => {
                        // will throw if message > 128kB
                        check_message_size(data);

                        return client.print(data);
                    })
                    .catch(e => {
                        // pass
                        errors.push(e);
                    });
            };
        });

        return print_recursive(callbacks)
            .then(() => {
                onSave(form);
            })
            .catch(e => {
                console.error(e);
                onSave(e);
                toast.error(t('ticket.printer.error_printing'));
            })
            .finally(() => {
                if (errors.length) {
                    console.error(errors);
                    toast.error(t('ticket.printer.multiple_errors_printing', { count: errors.length }));
                }
                setIsSubmitting(false);
            });
    }, [onSave, form, getDataCallback, client]);

    const not = value => {
        return value === null || value === undefined;
    };

    const print_jobs_count = Array.isArray(getDataCallback) ? getDataCallback.length : 1;

    return (
        <Modal
            size="medium"
            open={true}
            onClose={onCancel}
            title={t('ticket.manager.common.action.print_ticket.title')}
            subtitle={t('ticket.manager.common.action.print_ticket.subtitle')}
            submitButton={
                <Button
                    primary
                    onClick={handleSubmit}
                    disabled={isSubmitting || not(form.manufacturer) || not(form.support_type)}
                    isLoading={isSubmitting}
                    label={isSubmitting ? t('ticket.printer.print_in_progress', { count: print_jobs_count, current: currentPrintJob }) : t('ticket.common.action.confirm')}
                />
            }
            cancelButton={<Button onClick={onCancel} label={t(`ticket.common.action.cancel`)} />}
        >
            {!hasLoaded && t('ticket.printer.loading_printers')}
            {hasLoaded && <PrinterSelection printers={printers} onChange={setForm} form={form} client={client} />}
        </Modal>
    );
}

export function PrinterContextProvider({ children }) {
    const [{ organization }] = useAppContext();

    let [printerContext, setContext] = React.useState({
        printer_type: null,
        support_type: null
    });

    let [showPrinterModal, setShowPrinterModal] = React.useState(false);

    let onPrinterUpdate = React.useCallback(prints => {}, []);

    const get_token = React.useCallback(() => {
        if (!organization) {
            return null;
        }
        const endpoint = `/ticket/organizations/${organization.pk}/sign_token`;
        return fetcher
            .get(endpoint, { cache: false })
            .then(({ response: { access_token } }) => {
                return access_token;
            })
            .catch(e => {
                console.error('Error fetching sign_token:', e);
                // Return null instead of throwing to prevent app crash
                // This allows the app to continue without printer functionality
                return null;
            });
    }, [organization]);

    let client = React.useMemo(() => {
        if (!organization) {
            return null;
        }

        const client = new Client('wss://ws-weezbox.weezevent.com', get_token, onPrinterUpdate, process.env.NODE_ENV !== 'production', false);
        // Start client to get a single websocket
        client.start();

        return client;
    }, [onPrinterUpdate, get_token]);

    const onSaveModal = React.useCallback(
        (printer_type = null, support_type = null) => {
            setContext({
                printer_type: printer_type,
                support_type: support_type
            });
            setShowPrinterModal(false);
        },
        [setContext, client]
    );

    const onCancelModal = React.useCallback(() => {
        setShowPrinterModal(false);
    }, [setShowPrinterModal]);

    const print = React.useCallback(
        get_data_callback => {
            setShowPrinterModal(() => get_data_callback);
            return Promise.resolve();
        },
        [printerContext, client]
    );

    return (
        <PrinterContext.Provider
            value={{
                printerContext,
                client,
                print
            }}
        >
            {showPrinterModal && (
                <PrintDialogBase getDataCallback={showPrinterModal} printerContext={printerContext} client={client} onSave={onSaveModal} onCancel={onCancelModal} />
            )}
            {children}
        </PrinterContext.Provider>
    );
}

PrinterContextProvider.propTypes = {
    children: PropTypes.node.isRequired
};

export function usePrinterContext() {
    const context = React.useContext(PrinterContext);

    if (!context) {
        throw new Error('[usePrinterContext] Did you use usePrinterContext outside a PrinterContextProvider?');
    }
    return context;
}

export function PrintDialog({ ...props }) {
    const { client } = usePrinterContext();

    return <PrintDialogBase client={client} {...props} />;
}
